import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Target, BookOpen, Users, Shield, CheckCircle2, Clock, FileCheck2, AlertTriangle, ArrowRight, MonitorCheck } from 'lucide-react';

interface GouvernanceContratsBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}


type ContractScenario = {
  id: string;
  title: string;
  moment: string;
  intent: string;
  rules: string[];
  output: string;
  raci: { r: string; a: string; c: string; i: string };
};

const licenseTeam = [
  { name: 'Naomie', role: 'Licences', avatar: '/NB.png' },
  { name: 'LAH', role: 'Licences', avatar: '/LAH.jpg' },
  { name: 'Richard', role: 'Licences', avatar: '/RD.png' },
];

const procedureMetrics = [
  { label: 'Scénarios gouvernés', value: '8', tone: 'amber' },
  { label: 'Traçabilité cible', value: 'MonBZ', tone: 'cyan' },
  { label: 'Rappel renouvellement', value: '60 j', tone: 'emerald' },
  { label: 'Gel facturation', value: '1er', tone: 'rose' },
  { label: 'Retrait avant facture', value: '48 h', tone: 'violet' },
  { label: 'Majoration mensuelle', value: '+15 %', tone: 'orange' },
];

const contractScenarios: ContractScenario[] = [
  {
    id: '01',
    title: 'Signature du contrat',
    moment: 'Jour 0',
    intent: 'Cadrer les engagements Microsoft 365 dès la signature : produits, quantités approximatives, durée, rabais annuel et limites de retrait.',
    rules: [
      'Présenter les options annuelles par produit, incluant le rabais de 15 % vs le tarif mensuel.',
      'Confirmer que les licences sous engagement annuel sont fermes pour 12 mois et non créditables avant échéance.',
      'Informer le client que MonBZ centralise les produits, quantités, engagements et dates d’échéance.',
      'Préciser que le départ d’un client ne libère pas les engagements BZ/Microsoft déjà contractés.',
      'Expliquer la facturation du 1er du mois, le prorata applicable et les frais premier/dernier mois.',
    ],
    output: 'Contrat signé, engagements expliqués, visibilité MonBZ annoncée, règles de facturation comprises.',
    raci: { r: 'Directeur de compte', a: 'Client', c: 'Département licences / Prime Tech', i: 'Administration' },
  },
  {
    id: '02',
    title: 'Début du mandat',
    moment: 'Démarrage officiel',
    intent: 'Créer l’environnement opérationnel et contractuel : ODS, ouverture de compte, fiche client, contrat, tenant Microsoft 365 et consignes de facturation.',
    rules: [
      'Transmettre l’ODS au chargé de projet et les informations client à la facturation.',
      'Créer le contrat selon les informations validées à la signature et confirmées par le chargé de projet.',
      'Créer le tenant Microsoft 365 ou utiliser le tenant existant selon le contexte du client.',
      'Aviser la facturation du début du mandat, incluant les éléments à facturer ou exclure.',
    ],
    output: 'Fiche client, contrat, tenant et consignes de facturation prêts pour le démarrage.',
    raci: { r: 'Département licences + chargé de projet', a: 'Client', c: 'Directeur de compte / Tech Prime', i: 'Administration + directeur de compte' },
  },
  {
    id: '03',
    title: 'Ajout / retrait de licences',
    moment: 'En tout temps, sauf période de facturation',
    intent: 'Traiter les demandes de changement de licences avec autorisation, délais clairs, règles de facturation et journalisation MonBZ.',
    rules: [
      'Exiger la demande via MonBZ : produit, usagers, quantité, engagement mensuel/annuel, date souhaitée.',
      'Par défaut, une licence ajoutée en cours de contrat est mensuelle et majorée de 15 %, sauf engagement demandé.',
      'Aucune demande n’est traitée le 1er du mois; les retraits doivent être soumis 48 h avant la facturation.',
      'Les ajouts sont facturés au prorata; les retraits prennent effet au prochain cycle mensuel.',
      'Alerter le client après plus de 5 ajouts sur un même produit afin d’évaluer une conversion annuelle.',
    ],
    output: 'Modification autorisée, appliquée, facturée correctement et consignée dans les rapports mensuels.',
    raci: { r: 'Département licences', a: 'Client', c: 'Directeur de compte / Tech Prime', i: 'Administration + directeur de compte' },
  },
  {
    id: '04',
    title: 'Nouveaux produits',
    moment: 'En tout temps',
    intent: 'Ajouter un produit Microsoft non prévu au contrat initial avec avenant, signature et traitement structuré.',
    rules: [
      'Identifier le nouveau produit souhaité avec le client selon ses besoins.',
      'Préparer un avenant au contrat et le faire signer par le client et le directeur de compte.',
      'Transmettre la demande via MonBZ avec produit, quantité, engagement, date souhaitée et usagers concernés.',
      'S’assurer que l’avenant signé et les informations du produit sont visibles dans MonBZ.',
    ],
    output: 'Avenant signé, produit intégré au contrat, activation traitée comme ajout de licences.',
    raci: { r: 'Directeur de compte', a: 'Client', c: 'Département licences / Tech Prime', i: 'Administration' },
  },
  {
    id: '05',
    title: 'Préparation au renouvellement',
    moment: '60 jours avant échéance',
    intent: 'Lancer la révision annuelle des engagements pour reconduire, ajuster ou convertir les licences avant la nouvelle période.',
    rules: [
      'Envoyer un courriel automatisé hebdomadaire avec lien sécurisé vers MonBZ.',
      'Permettre au client de reconduire, ajuster, convertir en mensuel ou confirmer ses engagements annuels.',
      'Fournir les rapports de licences assignées, annexes habituelles et suggestions pertinentes.',
      'Archiver automatiquement les confirmations dans MonBZ; sans réponse, reconduire les engagements existants.',
    ],
    output: 'Décision de renouvellement tracée, engagements confirmés ou reconduits automatiquement.',
    raci: { r: 'Directeur de compte + département licences', a: 'Client', c: 'Département licences', i: 'Administration' },
  },
  {
    id: '06',
    title: 'Erreurs, exceptions et crédits',
    moment: 'Au besoin',
    intent: 'Corriger les erreurs d’abonnement et encadrer les crédits selon une procédure formelle, documentée et traçable.',
    rules: [
      'Identifier l’origine : BZ, client ou circonstance exceptionnelle.',
      'Si BZ est responsable : corriger, ajuster la facturation si applicable, générer un identifiant de correction et confirmer au client.',
      'Si le client est responsable : expliquer les limites contractuelles et accompagner la correction lorsque possible.',
      'Aucun crédit rétroactif n’est accordé hors procédure; les exceptions fournisseurs sont rares et non garanties.',
    ],
    output: 'Erreur classée, correction ou refus documenté, crédit validé uniquement si admissible.',
    raci: { r: 'Département licences', a: 'Département licences', c: 'Département concerné / directeur de compte', i: 'Client + administration' },
  },
  {
    id: '07',
    title: 'Suivi, transparence et rigueur opérationnelle',
    moment: 'En continu',
    intent: 'Maintenir une visibilité en temps réel sur les abonnements, les écarts, les modifications et les communications client.',
    rules: [
      'Donner au client un accès sécurisé à MonBZ : licences engagées/non engagées, produits, échéances, documents et historique.',
      'Permettre les demandes d’ajout/retrait avec statut de traitement automatisé.',
      'Produire une communication mensuelle : engagements, ajouts, anomalies et annexe de facture.',
      'Exiger les demandes de modification 2 jours ouvrables avant la fin du mois pour garantir le traitement avant facturation.',
    ],
    output: 'Client autonome, facturation fiable, historique complet et communications proactives.',
    raci: { r: 'Département licences', a: 'Client', c: 'Directeur de compte', i: 'Client + administration' },
  },
  {
    id: '08',
    title: 'Résiliation de contrat',
    moment: 'Fin de contrat ou demande client',
    intent: 'Analyser les engagements actifs, confirmer les impacts financiers et coordonner la désactivation des licences.',
    rules: [
      'Confirmer la demande de résiliation, la date de départ prévue et les informations requises pour le projet de départ.',
      'Vérifier les frais de départ, la date de fermeture du contrat et les dates de fin des abonnements Microsoft 365.',
      'Facturer les licences annuelles jusqu’à la fin de leur terme sur facture distincte.',
      'Désactiver le renouvellement automatique chez le fournisseur et vérifier les abonnements actifs et services liés.',
    ],
    output: 'Résiliation maîtrisée, engagements restants facturés, renouvellements désactivés et impacts communiqués.',
    raci: { r: 'Département licences', a: 'Client', c: 'Directeur de compte', i: 'Client + administration' },
  },
];

type RoadmapPillar = {
  id: string;
  pillar: string;
  owner: string;
  progress: number;
  focus: string;
  items: { label: string; status: 'done' | 'active' | 'next' | 'planned' }[];
};

const statusStyles = {
  done: 'border-emerald-400/50 bg-emerald-500/15 text-emerald-100',
  active: 'border-amber-400/60 bg-amber-500/20 text-amber-100',
  next: 'border-cyan-400/50 bg-cyan-500/15 text-cyan-100',
  planned: 'border-slate-500/50 bg-slate-700/40 text-slate-200',
};

const statusLabels = {
  done: 'fait',
  active: 'en cours',
  next: 'prochain',
  planned: 'planifié',
};

const roadmap2026: RoadmapPillar[] = [
  {
    id: 'gouvernance',
    pillar: 'Gouvernance',
    owner: 'Admin/RH + direction',
    progress: 45,
    focus: 'Clarifier qui porte quoi, comment on tranche les zones grises et quelles règles contractuelles ne sont jamais négociables.',
    items: [
      { label: 'Cadre holistique 8 axes documenté dans PRIsME', status: 'done' },
      { label: 'RACI de référence aligné sur la procédure Version 3.0', status: 'done' },
      { label: 'Points d’escalade et règles non négociables à publier aux équipes', status: 'active' },
      { label: 'Rituel trimestriel de revue contrats / irritants / exceptions', status: 'next' },
      { label: 'Tableau des décisions et exceptions à maintenir dans PRIsME', status: 'planned' },
    ],
  },
  {
    id: 'procedure',
    pillar: 'Procédure',
    owner: 'Naomie · LAH · Richard',
    progress: 70,
    focus: 'La procédure est montée et validée; le travail 2026 est maintenant de la communiquer, l’exécuter et l’améliorer avec les vrais irritants terrain.',
    items: [
      { label: 'Procédure Version 3.0 intégrée dans PRIsME', status: 'done' },
      { label: 'Validation faite par Naomie, LAH et Richard', status: 'done' },
      { label: 'Fichier Excel source disponible en téléchargement', status: 'done' },
      { label: 'Communication officielle aux parties prenantes', status: 'active' },
      { label: 'Mise en production de la procédure avec NORA ou autre support opérationnel', status: 'active' },
      { label: 'Boucle d’amélioration mensuelle sur exceptions, crédits, renouvellements et résiliations', status: 'next' },
    ],
  },
  {
    id: 'outils',
    pillar: 'Outils internes',
    owner: 'Tech Prime + MonBZ + NORA',
    progress: 20,
    focus: 'Faire travailler PRIsME, MonBZ, ERP, outils fournisseurs et NORA ensemble sans multiplier les doubles saisies.',
    items: [
      { label: 'PRIsME porte le cadre, la procédure et la source Excel', status: 'done' },
      { label: 'Définir le rôle exact de NORA dans l’exécution interne de la procédure', status: 'active' },
      { label: 'Cartographier où vit chaque donnée contractuelle : MonBZ, ERP, fournisseur, PRIsME', status: 'next' },
      { label: 'Créer les statuts simples de traitement : reçu, validé, traité, facturé, fermé', status: 'planned' },
      { label: 'Préparer les rapports standards : renouvellements, ajouts/retraits, anomalies', status: 'planned' },
    ],
  },
  {
    id: 'fournisseur',
    pillar: 'Fournisseur / Partenaire',
    owner: 'Licences + direction fournisseur',
    progress: 15,
    focus: 'Aligner les engagements clients avec les contraintes Microsoft et autres partenaires pour éviter les risques invisibles.',
    items: [
      { label: 'Identifier les contraintes fournisseurs critiques reflétées dans la procédure', status: 'done' },
      { label: 'Inventaire des fournisseurs récurrents par volume et risque', status: 'next' },
      { label: 'Documenter règles de durée, pénalités, crédits et changements de tarifs', status: 'planned' },
      { label: 'Créer une mini-carte de gouvernance fournisseur pour Microsoft 365', status: 'planned' },
      { label: 'Revue trimestrielle des risques fournisseur ↔ client', status: 'planned' },
    ],
  },
  {
    id: 'client',
    pillar: 'Client',
    owner: 'Directeurs de compte + licences',
    progress: 25,
    focus: 'Rendre les engagements compréhensibles dès la signature et donner au client des canaux clairs pour demander, confirmer et suivre.',
    items: [
      { label: 'Règles client intégrées dans la procédure : engagements, prorata, retraits, crédits', status: 'done' },
      { label: 'Préparer le message client/interne pour expliquer “la bonne façon”', status: 'active' },
      { label: 'Créer FAQ courte : ajout, retrait, crédit, renouvellement, résiliation', status: 'next' },
      { label: 'Aligner MonBZ avec les demandes et confirmations client', status: 'planned' },
      { label: 'Mesurer les irritants contrats/facturation après mise en production', status: 'planned' },
    ],
  },
  {
    id: 'contrat',
    pillar: 'Contrat',
    owner: 'Directeurs de compte + administration',
    progress: 20,
    focus: 'S’assurer que le contrat, les avenants et les confirmations supportent la procédure au lieu de créer des exceptions floues.',
    items: [
      { label: 'Scénarios contrat couverts : signature, nouveaux produits, renouvellement, résiliation', status: 'done' },
      { label: 'Identifier les clauses ou avenants à ajuster selon la procédure', status: 'next' },
      { label: 'Standardiser les preuves : autorisation, avenant, confirmation, exception', status: 'planned' },
      { label: 'Relier les changements contractuels à la facturation et aux rapports', status: 'planned' },
      { label: 'Revue des contrats à risque ou non alignés avec la procédure', status: 'planned' },
    ],
  },
  {
    id: 'communication',
    pillar: 'Communication',
    owner: 'Admin/RH + licences + directeurs de compte',
    progress: 30,
    focus: 'C’est la prochaine étape prioritaire : annoncer la procédure, clarifier les canaux et éviter les promesses ou demandes hors cadre.',
    items: [
      { label: 'Préparer communication officielle aux départements et parties prenantes', status: 'active' },
      { label: 'Définir les canaux autorisés : MonBZ, courriel structuré, téléphone consigné', status: 'active' },
      { label: 'Créer gabarits : autorisation, renouvellement, crédit, exception, résiliation', status: 'next' },
      { label: 'Former les équipes sur “quoi répondre” et “quoi ne jamais promettre”', status: 'next' },
      { label: 'Mettre en place un journal central des décisions / exceptions', status: 'planned' },
    ],
  },
  {
    id: 'finances',
    pillar: 'Finances, marges & orphelins',
    owner: 'Finance + direction',
    progress: 10,
    focus: 'Voir rapidement les écarts fournisseur-client, les crédits, les orphelins et les contrats non rentables.',
    items: [
      { label: 'Règles de facturation critiques intégrées dans la procédure', status: 'done' },
      { label: 'Définir KPIs : marge, orphelins, crédits, écarts, renouvellements à risque', status: 'next' },
      { label: 'Créer rapport mensuel contrats/licences pour Finance et CE', status: 'planned' },
      { label: 'Processus d’ajustement quand un contrat devient non rentable', status: 'planned' },
      { label: 'Revue trimestrielle des impacts financiers fournisseurs et clients', status: 'planned' },
    ],
  },
]

const roadmapQuarters = [
  { quarter: 'Q1', title: 'Cadrer', text: 'Cadre, procédure, RACI et source Excel consolidés.' },
  { quarter: 'Q2', title: 'Communiquer', text: 'Annonce aux parties prenantes, formation courte et gabarits essentiels.' },
  { quarter: 'Q3', title: 'Produire', text: 'NORA + MonBZ en soutien opérationnel, suivi des demandes et preuves.' },
  { quarter: 'Q4', title: 'Mesurer', text: 'KPIs, irritants, exceptions, marges et amélioration 2027.' },
];


export default function GouvernanceContratsBZModal({
  isOpen,
  onClose,
}: GouvernanceContratsBZModalProps) {
  const [activeTab, setActiveTab] = useState<
    'esprit' | 'onepager' | 'cadre' | 'procedure' | 'maturite' | 'raci' | 'roadmap' | 'rituels'
  >('esprit');
  const [expandedAxis, setExpandedAxis] = useState<
    | 'gouvernance'
    | 'procedure'
    | 'outils'
    | 'fournisseur'
    | 'client'
    | 'contrat'
    | 'communication'
    | 'finances'
    | null
  >(null);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-amber-500/40 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-amber-500/40 bg-slate-950/70 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-amber-500/20 border border-amber-400/60 flex items-center justify-center shadow-lg">
                <Target className="w-6 h-6 text-amber-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold">
                  Esprit & Objectifs Macro
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  Gouvernance Gestion des contrats BZ
                </h2>
                <p className="text-xs md:text-sm text-amber-100/80 mt-1">
                  Vision contractuelle de BZ : transformer chaque engagement en structure exploitable, mesurable et rentable.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 ml-4">
              <div className="flex items-center gap-1" title="Impliqués – Gestion des contrats BZ">
                {[
                  { name: 'Mélissa', avatar: '/MP.png' },
                  { name: 'LAH', avatar: '/LAH.jpg' },
                  { name: 'Richard', avatar: '/RD.png' },
                  { name: 'François', avatar: '/FP.png' },
                  { name: 'Dany', avatar: '/DB.png' },
                ].map((p) => (
                  <img
                    key={p.name}
                    src={p.avatar}
                    alt={p.name}
                    className="w-8 h-8 rounded-full border-2 border-amber-400/80 object-cover shadow-sm hover:border-amber-300"
                    title={p.name}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/20 p-2 text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Sélecteur d'onglets */}
          <div className="px-6 pt-4 border-b border-amber-500/30 bg-slate-950/60">
            <div className="flex justify-center">
              <div className="inline-flex rounded-xl border border-amber-500/40 bg-slate-900/60 p-1 text-xs md:text-sm">
                {(
                  [
                    { id: 'esprit', label: 'Esprit & Objectifs macro' },
                    { id: 'onepager', label: 'Politique simplifiée' },
                    { id: 'cadre', label: 'Cadre holistique' },
                    { id: 'procedure', label: 'Procédure contrats' },
                    { id: 'maturite', label: 'Maturité' },
                    { id: 'raci', label: 'Rôles & responsabilités' },
                    { id: 'roadmap', label: 'Roadmap' },
                    { id: 'rituels', label: 'Gouvernance & rituels' },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-semibold transition ${
                      activeTab === tab.id
                        ? 'bg-amber-500/30 text-white border border-amber-400/70 shadow-sm'
                        : 'text-amber-100/80 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 text-sm md:text-[0.9rem] text-slate-100">
            {activeTab === 'esprit' && (
              <>
                {/* Vision de la fonction Contrats */}
                <section className="space-y-3">
              <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                <BookOpen className="w-5 h-5" />
                Vision de la fonction Contrats
              </h3>
              <p className="text-slate-100/90">
                Chez BZ, un contrat n&apos;est <span className="font-semibold">pas</span> juste un papier légal. C&apos;est un{' '}
                <span className="font-semibold">actif opérationnel</span> qui structure la relation avec le client, les fournisseurs
                et les équipes internes.
              </p>
              <p className="text-slate-100/90">
                Chaque contrat est le point de départ de la{' '}
                <span className="font-semibold">facturation récurrente</span> et des{' '}
                <span className="font-semibold">engagements de BZ et de ses fournisseurs</span>.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mt-2">
                <div className="bg-slate-900/60 border border-slate-700/70 rounded-xl p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold">
                    Le contrat alimente
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90 text-[0.9rem]">
                    <li>la facturation récurrente client</li>
                    <li>les engagements de BZ et de ses fournisseurs</li>
                    <li>la définition des produits et services livrés</li>
                    <li>la définition des SLO/SLA</li>
                    <li>le calcul de la marge par client ou par programme</li>
                    <li>le déclenchement des workflows opérationnels</li>
                  </ul>
                </div>

                <div className="bg-slate-900/60 border border-rose-700/70 rounded-xl p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-rose-300 font-semibold">
                    Sans gouvernance contractuelle
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90 text-[0.9rem]">
                    <li>la facturation devient approximative</li>
                    <li>les engagements restent implicites</li>
                    <li>les marges deviennent invisibles</li>
                    <li>les workflows deviennent arbitraires</li>
                    <li>la rentabilité devient une opinion, pas une mesure</li>
                  </ul>
                </div>
              </div>

              <p className="text-slate-100/90">
                La fonction Contrats fait le lien entre{' '}
                <span className="font-semibold">ce qui est vendu</span>,{' '}
                <span className="font-semibold">ce qui est livré</span>,{' '}
                <span className="font-semibold">ce qui est facturé</span>,{' '}
                <span className="font-semibold">ce que ça coûte</span> et{' '}
                <span className="font-semibold">ce que ça rapporte</span>. Elle devient la{' '}
                <span className="font-semibold">source de vérité économique</span> de BZ.
              </p>

              <div className="bg-amber-900/40 border border-amber-500/60 rounded-xl p-4 mt-2">
                <p className="text-xs uppercase tracking-wide text-amber-200 font-semibold mb-1">
                  Règle d&apos;or
                </p>
                <p className="text-amber-50 font-semibold">
                  Tout ce qui génère ou consomme de la valeur doit être{' '}
                  <span className="underline decoration-amber-300">structuré contractuellement</span> avant d&apos;être
                  exécuté ou facturé.
                </p>
              </div>
            </section>

            {/* Vision */}
            <section className="space-y-3">
              <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                <Target className="w-5 h-5" />
                Vision
              </h3>
              <p className="text-slate-100/90">
                Transformer chaque engagement commercial en une{' '}
                <span className="font-semibold">structure exploitable, mesurable et automatisable</span>.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mt-2">
                <div className="bg-slate-900/60 border border-slate-700/70 rounded-xl p-4 space-y-1 text-[0.9rem]">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold mb-1">
                    Unité de gestion
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90">
                    <li>unité de gestion financière</li>
                    <li>clé d&apos;accès à la facturation</li>
                    <li>conteneur de marge</li>
                  </ul>
                </div>
                <div className="bg-slate-900/60 border border-slate-700/70 rounded-xl p-4 space-y-1 text-[0.9rem]">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold mb-1">
                    Déclencheur de processus
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90">
                    <li>déclencheur des workflows opérationnels</li>
                    <li>cadre pour les renouvellements</li>
                  </ul>
                </div>
                <div className="bg-slate-900/60 border border-slate-700/70 rounded-xl p-4 space-y-1 text-[0.9rem]">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold mb-1">
                    Donnée stratégique
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90">
                    <li>donnée structurante pour l&apos;analyse de performance</li>
                    <li>base de la rentabilité par client / produit</li>
                    <li>support aux décisions de portefeuille</li>
                  </ul>
                </div>
              </div>

              <p className="text-slate-100/90">
                Nous ne gérons pas des documents. Nous structurons la{' '}
                <span className="font-semibold">capacité de BZ à générer une rentabilité durable</span>.
              </p>
                </section>

            {/* Objectifs macro */}
                <section className="space-y-3">
              <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                <Target className="w-5 h-5" />
                Objectifs macro 2026
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-900/60 border border-amber-500/40 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold mb-2">
                    Objectifs acquis
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90 text-[0.9rem]">
                    <li>100 % des revenus récurrents adossés à un contrat structuré</li>
                    <li>100 % des contrats liés à un produit standard BZ</li>
                    <li>100 % des facturations récurrentes déclenchées à partir du contrat</li>
                    <li>Rentabilité mesurable par client</li>
                    <li>Alignement client ↔ fournisseur dans un même modèle contractuel</li>
                  </ul>
                </div>
                <div className="bg-slate-900/60 border border-amber-500/40 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold mb-2">
                    Objectifs 2026
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-100/90 text-[0.9rem]">
                    <li>Créer et implanter une procédure claire de gestion opérationnelle des contrats</li>
                    <li>Activation automatisée des workflows</li>
                    <li>Source unique de vérité pour les engagements financiers et légaux</li>
                    <li>Rentabilité mesurable par produit</li>
                    <li>Dashboard client que le client peut gérer lui-même</li>
                  </ul>
                </div>
              </div>
                </section>

                {/* Enjeux actuels à améliorer */}
                <section className="space-y-3">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                    <Target className="w-5 h-5" />
                    Enjeux actuels à améliorer
                  </h3>
                  <div className="bg-slate-900/60 border border-amber-500/40 rounded-xl p-4">
                    <ul className="list-disc list-inside space-y-1 text-slate-100/90 text-[0.9rem]">
                      <li>
                        Standardisation du processus de gestion des contrats, de la création à la modification,
                        jusqu&apos;à la fermeture ou au renouvellement.
                      </li>
                      <li>
                        Insatisfaction clientèle liée aux délais de traitement (ex.: demandes d&apos;ajustement de licences)
                        et au manque de clarté sur les engagements contractuels.
                      </li>
                      <li>
                        Incompréhension des engagements de BZ et du client (formation/explication incomplète,
                        RACI non compris côté client et en interne).
                      </li>
                      <li>
                        Mauvais canaux de communication (multiples points d&apos;entrée, demandes qui se perdent ou se
                        doublent).
                      </li>
                      <li>
                        Insatisfaction inter-départements liée aux attentes non alignées et au flou contractuel.
                      </li>
                      <li>
                        Enjeu majeur : <span className="font-semibold">COMMUNICATION</span> en général (messages, canaux,
                        responsabilités, attentes et suivis).
                      </li>
                      <li>
                        Adoption de la gouvernance : faire vivre les règles et processus contractuels au quotidien
                        (pas seulement les documenter).
                      </li>
                    </ul>
                  </div>
                </section>
              </>
            )}

            {activeTab === 'onepager' && (
              <section className="flex justify-center">
                <article className="w-full max-w-4xl rounded-sm bg-white px-8 py-7 text-[0.86rem] leading-relaxed text-slate-950 shadow-2xl ring-1 ring-slate-200 md:px-12 md:py-10">
                  <header className="relative mb-6 text-center">
                    <img
                      src="/BZ.png"
                      alt="BZ inc."
                      className="absolute left-0 top-0 h-12 w-auto object-contain"
                    />
                    <p className="font-bold text-slate-950">Politique simplifiée de gestion des employés internes</p>
                    <h3 className="mt-8 text-lg font-black uppercase tracking-wide text-slate-950">
                      Gestion des contrats BZ
                    </h3>
                    <a
                      href="/documents/admin-rh/politique-simplifiee-gestion-contrats-bz.pdf"
                      download
                      className="mt-4 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[0.7rem] font-bold uppercase tracking-wide text-slate-800 transition hover:border-slate-400 hover:bg-slate-200 md:absolute md:right-0 md:top-0 md:mt-0"
                    >
                      <FileCheck2 className="h-4 w-4" />
                      Télécharger le document PDF
                    </a>
                  </header>

                  <div className="space-y-4">
                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Principes :</h4>
                      <p className="pl-8 text-justify">
                        Cette politique encadre la gestion simple, claire et traçable des contrats BZ, de la signature
                        jusqu&apos;au renouvellement ou à la fermeture.
                      </p>
                    </section>

                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Objectifs :</h4>
                      <div className="space-y-1 pl-8 text-justify">
                        <p>Standardiser la création, la modification, le renouvellement et la fermeture des contrats;</p>
                        <p>Clarifier les responsabilités et réduire les exceptions non documentées;</p>
                        <p>Assurer la traçabilité des engagements clients, fournisseurs et BZ.</p>
                      </div>
                    </section>

                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Champ d&apos;application :</h4>
                      <p className="pl-8 text-justify">
                        Cette politique vise l&apos;ensemble des employés INTERNES de BZ inc. impliqués dans les contrats,
                        licences, facturation, ventes, opérations ou relation client.
                      </p>
                    </section>

                    <section className="space-y-2">
                      <h4 className="font-bold text-slate-950">Politique :</h4>
                      <p className="pl-8 text-justify">Le mécanisme de gestion des contrats chez BZ inc. est simple :</p>
                      <ul className="ml-8 list-disc space-y-1 pl-5 text-justify">
                        <li>
                          À la signature ou au renouvellement : les produits, quantités, prix, dates, responsabilités
                          et engagements doivent être confirmés;
                        </li>
                        <li>
                          Pendant la vie du contrat : les ajouts, retraits, crédits et exceptions doivent être documentés
                          et validés dans le bon canal;
                        </li>
                        <li>
                          À la fermeture : les impacts clients, fournisseurs, facturation et licences doivent être vérifiés
                          avant toute confirmation finale.
                        </li>
                      </ul>
                      <p className="pl-8 text-justify">
                        Toute demande ou tout changement qui pourrait modifier un engagement contractuel, la facturation,
                        les licences, un crédit, une exception, un renouvellement ou la relation client doit être validé
                        avant exécution par les responsables concernés. La demande doit préciser le produit ou service visé,
                        la quantité, la date souhaitée, l&apos;impact sur la facturation, l&apos;engagement client/fournisseur
                        et l&apos;approbation requise. La décision doit ensuite être documentée dans le dossier client afin
                        d&apos;assurer la traçabilité.
                      </p>
                    </section>

                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Responsable de l&apos;application de la procédure :</h4>
                      <div className="space-y-1.5 pl-8 text-justify">
                        <p>Richard Delisle est responsable imputable de la gouvernance des contrats et des licences.</p>
                        <p>
                          L&apos;administration assure l&apos;application opérationnelle de la procédure, notamment le suivi
                          des dossiers, la facturation, les preuves et la documentation.
                        </p>
                        <p>
                          Les directeurs de compte sont responsables de transmettre l&apos;information client, de faire
                          valider les changements et de maintenir une communication claire avec le client.
                        </p>
                        <p>
                          François Delisle doit être consulté pour les exceptions majeures, les impacts financiers
                          importants ou les zones grises.
                        </p>
                      </div>
                    </section>

                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Révision :</h4>
                      <p className="pl-8 text-justify">
                        Cette politique sera révisée au besoin, notamment lors d&apos;un changement majeur aux outils,
                        fournisseurs ou processus de facturation.
                      </p>
                    </section>

                    <section className="space-y-1.5">
                      <h4 className="font-bold text-slate-950">Entrée en vigueur :</h4>
                      <p className="pl-8 text-justify">La présente politique entre en vigueur immédiatement.</p>
                    </section>
                  </div>
                </article>
              </section>
            )}

            {activeTab === 'cadre' && (
              <section className="space-y-4">
                <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                  <BookOpen className="w-5 h-5" />
                  Cadre holistique – Gestion des contrats BZ
                </h3>
                <p className="text-slate-100/90">
                  8 axes pour voir en un coup d&apos;œil si on couvre tout : gouvernance, procédure, outils, fournisseurs,
                  clients, contrats, communication et finances.
                </p>

                <div className="rounded-xl p-3 mb-4 bg-amber-500/20 border-2 border-amber-400/70 text-center">
                  <p className="text-amber-100 font-semibold text-sm">
                    Priorité #1 année 2026 : l&apos;axe <span className="uppercase font-bold text-amber-200">Procédure</span> corrige ~80% des problèmes actuels. À traiter en premier.
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    {
                      id: 'gouvernance' as const,
                      title: 'Gouvernance',
                      subtitle:
                        'Qui porte quoi sur les contrats, comment on tranche les zones grises et quelles règles ne sont jamais négociables.',
                      tasks: [
                        'Définir un RACI simple par axe de ce cadre de gouvernance.',
                        'Documenter 3–5 règles non négociables (rien sans contrat, pas de rétro-crédit hors procédure, etc.).',
                        'Préciser le point d’escalade final en cas de conflit ou zone grise.',
                        'Aligner ce cadre avec la gouvernance Admin/RH et Finance existante.',
                        'Établir un rituel de rencontre trimestriel / postmortem et définir les bons participants.',
                      ],
                    },
                    {
                      id: 'procedure' as const,
                      title: 'Procédure',
                      subtitle:
                        'Le cycle de vie simple mais complet : signature, vie du contrat, ajouts/retraits, renouvellement et sortie client.',
                      tasks: [
                        'Bâtir la procédure contrats (onglet suivant de cette gouvernance) qui englobe clairement les 5 sous-tâches ci-dessous.',
                        'Valider le workflow signature → vie du contrat → renouvellement → sortie client pour chaque produit clé.',
                        'S’assurer que chaque scénario critique (erreur, crédit, exceptions) a une procédure claire.',
                        'Synchroniser la procédure avec MonBZ (ce qui se fait dans la plateforme vs à l’interne).',
                        'Limiter les variantes : une seule “bonne façon” pour chaque type de demande.',
                        'Relier la procédure aux bons rituels (revues mensuelles, trimestrielles).',
                      ],
                    },
                    {
                      id: 'outils' as const,
                      title: 'Outils internes',
                      subtitle:
                        'Synapse, Germana, ERPBZ, MonBZ et PRIsME qui travaillent ensemble : back-office, portail client et référentiel de gouvernance.',
                      tasks: [
                        'Clarifier le rôle de chaque outil « Vue d’ensemble » (Synapse, Germana, ERPBZ, MonBZ, PRIsME) dans le cycle de vie des contrats.',
                        'S’assurer que la même donnée contractuelle n’est pas saisie 3 fois dans 3 systèmes.',
                        'Dashboard client que le client peut gérer lui-même (MonBZ comme vue simple mais complète).',
                        'Mettre en place quelques rapports standard (contrats actifs, orphelins, renouvellements à venir).',
                        'Identifier les intégrations à bâtir à moyen terme (Synapse ↔ Germana ↔ ERPBZ ↔ MonBZ ↔ PRIsME ↔ API fournisseurs externes).',
                      ],
                    },
                    {
                      id: 'fournisseur' as const,
                      title: 'Fournisseur / Partenaire',
                      subtitle:
                        'Comment BZ respecte et optimise ses engagements envers Microsoft, Cloud BZ et les autres partenaires.',
                      tasks: [
                        'Faire l’inventaire des fournisseurs récurrents et les prioriser selon le volume $ annuel.',
                        'Créer une carte de gouvernance dans PRIsME (département Admin) par partenaire/fournisseur (engagements, contraintes, règles du jeu).',
                        'Aligner les contrats clients sur les contraintes fournisseurs (quantités, durées, pénalités).',
                        'Définir les procédures d’onboarding/offboarding fournisseur (entrée dans l’écosystème et sortie propre).',
                        'Définir la procédure interne en cas de changement de tarifs ou de politiques fournisseurs.',
                        'Identifier les zones où BZ prend du risque (différence entre ce qu’on paye et ce qu’on facture).',
                      ],
                    },
                    {
                      id: 'client' as const,
                      title: 'Client',
                      subtitle:
                        'Comment le client comprend ses engagements, voit ses licences et interagit avec nous via MonBZ sans zone grise.',
                      tasks: [
                        'Standardiser comment on explique les engagements et règles au moment de la signature.',
                        'Définir ce que le client voit / peut faire dans MonBZ (lecture, demandes, confirmations).',
                        'Prévoir un parcours clair pour les demandes complexes (nouveaux produits, crédits, exceptions).',
                        'S’assurer que la documentation client (FAQ, guides) reflète la vraie gouvernance contrats.',
                        'Mesurer la satisfaction client spécifiquement sur la dimension “contrats & facturation”.',
                      ],
                    },
                    {
                      id: 'contrat' as const,
                      title: 'Contrat',
                      subtitle:
                        'La structure même du contrat : clauses clés, liens produits/licences, avenants et ce qui déclenche la facturation.',
                      tasks: [
                        'Standardiser la traçabilité de la source des changements aux contrats.',
                        'Documenter quelles clauses déclenchent des workflows (renouvellement, indexation, pénalités).',
                        'S’assurer que le contrat supporte l’analyse de marge et d’orphelins sans gymnastique Excel.',
                      ],
                    },
                    {
                      id: 'communication' as const,
                      title: 'Communication',
                      subtitle:
                        'Les workflows et canaux officiels pour les demandes, confirmations, changements, erreurs et crédits.',
                      tasks: [
                        'Définir les canaux autorisés (MonBZ, courriel structuré, téléphone consigné) et ceux à éviter.',
                        'Dessiner un workflow de communication simple (qui parle à qui, par quel canal, à quel moment).',
                        'Concevoir quelques gabarits de courriels clés (autorisation, renouvellement, crédit, exception).',
                        'Mettre en place un journal centralisé des décisions / exceptions contractuelles.',
                        'Former les équipes internes sur “quoi répondre” et “quoi ne jamais promettre” côté contrats.',
                        'Aligner la communication avec les rituels (mensuel, trimestriel) et les rapports envoyés au client.',
                      ],
                    },
                    {
                      id: 'finances' as const,
                      title: 'Finances, marges & orphelins',
                      subtitle:
                        'Comment on s’assure que chaque contrat rapporte ce qu’il doit : marge visible, orphelins détectés et décisions éclairées.',
                      tasks: [
                        'Définir les KPIs de base : marge par contrat, nombre d’orphelins, écarts fournisseur↔client.',
                        'Mettre en place un rapport mensuel simple pour suivre ces KPIs et les partager avec Finance / CE.',
                        'Standardiser la façon d’analyser un contrat non rentable (checklist de questions).',
                        'Prévoir un processus d’ajustement (augmentation, rationalisation, sortie) quand un contrat dérape.',
                        'Relier ces KPIs aux décisions stratégiques (portefeuille produits, offres, politique de rabais).',
                      ],
                    },
                  ].map((axis) => (
                    <div
                      key={axis.id}
                      className={
                        axis.id === 'procedure'
                          ? 'bg-gradient-to-r from-amber-500/30 via-amber-600/25 to-amber-500/30 border-2 border-amber-400 rounded-xl overflow-hidden shadow-lg shadow-amber-500/20'
                          : 'bg-slate-900/80 border border-amber-500/40 rounded-xl overflow-hidden'
                      }
                    >
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition ${
                          axis.id === 'procedure' ? 'hover:bg-amber-500/15' : 'hover:bg-slate-900'
                        }`}
                        onClick={() => setExpandedAxis((prev) => (prev === axis.id ? null : axis.id))}
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-xs uppercase tracking-wide text-amber-300 font-semibold">
                              {axis.title}
                            </p>
                            {axis.id === 'procedure' && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-400/90 text-slate-900 text-[0.65rem] font-bold uppercase">
                                Priorité #1 2026 · ~80% des problèmes
                              </span>
                            )}
                          </div>
                          <p className="text-[0.9rem] text-slate-100/90">{axis.subtitle}</p>
                          {axis.id === 'gouvernance' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/RD.png"
                                  alt="Richard Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/FP.png"
                                  alt="François Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'procedure' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/RD.png"
                                  alt="Richard Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/LAH.jpg"
                                  alt="Louis-Alexandre Hébert"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/NB.png"
                                  alt="Naomie"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/AG-transparent.png"
                                  alt="Antoine Giroux"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  C
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'outils' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/ED.png"
                                  alt="Éric Durand"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/FP.png"
                                  alt="François Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/NB.png"
                                  alt="Naomie"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'fournisseur' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/DB.png"
                                  alt="Dany Bourassa"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/AG-transparent.png"
                                  alt="Antoine Giroux"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'client' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/AG-transparent.png"
                                  alt="Antoine Giroux"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/LAH.jpg"
                                  alt="Louis-Alexandre Hébert"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/NB.png"
                                  alt="Naomie"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'contrat' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/RD.png"
                                  alt="Richard Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/LAH.jpg"
                                  alt="Louis-Alexandre Hébert"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/NB.png"
                                  alt="Naomie"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'communication' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/FP.png"
                                  alt="François Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/NB.png"
                                  alt="Naomie"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/LAH.jpg"
                                  alt="Louis-Alexandre Hébert"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/AG-transparent.png"
                                  alt="Antoine Giroux"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                          {axis.id === 'finances' && (
                            <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-slate-200">
                              <div className="flex items-center gap-1">
                                <img
                                  src="/FP.png"
                                  alt="François Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  A
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/RD.png"
                                  alt="Richard Delisle"
                                  className="w-6 h-6 rounded-full border border-amber-400 object-cover"
                                />
                                <span className="px-1.5 py-0.5 rounded-full border border-amber-400/60 text-amber-200">
                                  R
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <span className="text-amber-300 text-xs font-semibold">
                          {expandedAxis === axis.id ? '−' : '+'}
                        </span>
                      </button>
                      {expandedAxis === axis.id && (
                        <div className="border-t border-amber-500/30 bg-slate-950/80 px-4 py-3">
                          {axis.id === 'procedure' ? (
                            <div className="space-y-2 text-[0.85rem] text-slate-100/90">
                              <div className="rounded-lg border border-amber-400/70 bg-amber-500/10 px-3 py-2 font-semibold">
                                {axis.tasks[0]}
                              </div>
                              <ul className="list-disc list-inside space-y-1 border-l border-amber-500/40 ml-2 pl-3">
                                {axis.tasks.slice(1).map((task) => (
                                  <li key={task}>{task}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <ul className="list-disc list-inside text-[0.85rem] text-slate-100/90 space-y-1">
                              {axis.tasks.map((task) => (
                                <li key={task}>{task}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'procedure' && (
              <section className="space-y-6">
                <div className="relative overflow-hidden rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-slate-900 to-cyan-500/10 p-5 shadow-2xl">
                  <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl" />
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.35em] text-amber-200">Version 3.0 · Procédure validée</p>
                      <h3 className="mt-2 flex items-center gap-2 text-2xl font-black text-white">
                        <MonitorCheck className="h-6 w-6 text-amber-200" />
                        Renouvellement Microsoft 365 — contrats, licences & MonBZ
                      </h3>
                      <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-200">
                        Refonte de la procédure approuvée par le département licences. L’objectif est de rendre la mécanique contractuelle simple à comprendre :
                        qui fait quoi, à quel moment, avec quelle preuve, et comment MonBZ devient le point de traçabilité opérationnelle.
                      </p>
                    </div>
                    <div className="shrink-0 space-y-3">
                      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4">
                        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-emerald-200">Approuvé par</p>
                        <div className="flex gap-3">
                          {licenseTeam.map((person) => (
                            <div key={person.name} className="text-center">
                              <img src={person.avatar} alt={person.name} className="mx-auto h-12 w-12 rounded-2xl border-2 border-emerald-300 object-cover shadow-lg" />
                              <p className="mt-1 text-xs font-bold text-white">{person.name}</p>
                              <p className="text-[10px] text-emerald-100/80">{person.role}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <a
                        href="/documents/admin-rh/procedure-renouvellement-microsoft365-bz-raci-v3.xlsx"
                        download
                        className="flex items-center justify-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-3 text-xs font-black uppercase tracking-wide text-amber-100 transition hover:border-amber-200 hover:bg-amber-400/25"
                      >
                        <FileCheck2 className="h-4 w-4" />
                        Télécharger le fichier Excel source
                      </a>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                  {procedureMetrics.map((metric) => (
                    <div key={metric.label} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center shadow-lg">
                      <p className="text-2xl font-black text-white">{metric.value}</p>
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-300">{metric.label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
                  <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5">
                    <div className="mb-5 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-200">Vue d’ensemble</p>
                        <h4 className="mt-1 text-xl font-black text-white">Cycle contractuel Microsoft 365</h4>
                      </div>
                      <span className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-xs font-bold text-cyan-100">MonBZ = source de vérité</span>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      {contractScenarios.map((scenario, index) => (
                        <div key={scenario.id} className="group rounded-2xl border border-white/10 bg-slate-950/70 p-4 transition hover:border-amber-300/60 hover:bg-slate-900">
                          <div className="flex items-start gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-500/20 text-sm font-black text-amber-100 ring-1 ring-amber-300/30">{scenario.id}</span>
                            <div>
                              <p className="font-bold text-white">{scenario.title}</p>
                              <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-200"><Clock className="h-3.5 w-3.5" />{scenario.moment}</p>
                            </div>
                          </div>
                          <p className="mt-3 text-xs leading-5 text-slate-300">{scenario.intent}</p>
                          {index < contractScenarios.length - 1 && (
                            <div className="mt-3 hidden items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 md:flex">
                              <span>Étape suivante</span><ArrowRight className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-3xl border border-amber-400/25 bg-amber-500/10 p-5">
                      <h4 className="flex items-center gap-2 text-lg font-black text-amber-100"><Shield className="h-5 w-5" />Règles de contrôle</h4>
                      <ul className="mt-4 space-y-3 text-sm text-slate-200">
                        {[
                          'Toute demande opérationnelle passe par MonBZ lorsque disponible.',
                          'Aucune modification critique pendant la journée de facturation du 1er.',
                          'Les retraits doivent être soumis 48 h avant la facturation.',
                          'Les licences annuelles sont fermes pour 12 mois.',
                          'Aucun crédit rétroactif hors procédure validée.',
                          'Chaque changement doit être visible dans les rapports mensuels et annexes de facture.',
                        ].map((rule) => (
                          <li key={rule} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />{rule}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-3xl border border-rose-400/25 bg-rose-500/10 p-5">
                      <h4 className="flex items-center gap-2 text-lg font-black text-rose-100"><AlertTriangle className="h-5 w-5" />Points sensibles</h4>
                      <p className="mt-3 text-sm leading-6 text-slate-200">
                        Les notes du fichier indiquent certains sujets à finaliser : niveau exact d’automatisation MonBZ, nécessité du courriel d’autorisation lorsque l’accès MonBZ est déjà contrôlé, seuil minimal du rapport d’écarts et RACI des communications client/tickets.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5">
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-200">RACI opérationnel</p>
                      <h4 className="mt-1 text-xl font-black text-white">Responsabilités par scénario</h4>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                      <span className="rounded-full bg-sky-500/20 px-3 py-1 text-sky-100">R Responsable</span>
                      <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-emerald-100">A Approbateur</span>
                      <span className="rounded-full bg-amber-500/20 px-3 py-1 text-amber-100">C Consulté</span>
                      <span className="rounded-full bg-violet-500/20 px-3 py-1 text-violet-100">I Informé</span>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr] bg-slate-950/90 text-[11px] font-black uppercase tracking-wide text-slate-300">
                      <div className="p-3">Scénario</div><div className="p-3">R</div><div className="p-3">A</div><div className="p-3">C</div><div className="p-3">I</div>
                    </div>
                    {contractScenarios.map((scenario) => (
                      <div key={scenario.id} className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr] border-t border-white/10 text-xs text-slate-200">
                        <div className="p-3 font-bold text-white"><span className="text-amber-200">{scenario.id}</span> · {scenario.title}</div>
                        <div className="p-3 text-sky-100">{scenario.raci.r}</div>
                        <div className="p-3 text-emerald-100">{scenario.raci.a}</div>
                        <div className="p-3 text-amber-100">{scenario.raci.c}</div>
                        <div className="p-3 text-violet-100">{scenario.raci.i}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {contractScenarios.map((scenario) => (
                    <div key={scenario.id} className="rounded-3xl border border-white/10 bg-slate-900/70 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-200">Scénario {scenario.id} · {scenario.moment}</p>
                          <h4 className="mt-1 text-xl font-black text-white">{scenario.title}</h4>
                          <p className="mt-2 max-w-5xl text-sm leading-6 text-slate-300">{scenario.intent}</p>
                        </div>
                        <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-xs font-bold text-emerald-100">Livrable : {scenario.output}</span>
                      </div>
                      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_320px]">
                        <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-4">
                          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-amber-100"><FileCheck2 className="h-4 w-4" />Étapes et règles clés</p>
                          <ul className="space-y-2 text-sm leading-6 text-slate-200">
                            {scenario.rules.map((rule) => (
                              <li key={rule} className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />{rule}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4">
                          <p className="mb-3 text-sm font-black text-amber-100">RACI</p>
                          <div className="space-y-2 text-xs leading-5 text-slate-200">
                            <p><span className="font-black text-sky-200">R</span> · {scenario.raci.r}</p>
                            <p><span className="font-black text-emerald-200">A</span> · {scenario.raci.a}</p>
                            <p><span className="font-black text-amber-200">C</span> · {scenario.raci.c}</p>
                            <p><span className="font-black text-violet-200">I</span> · {scenario.raci.i}</p>
                          </div>
                          <div className="mt-4 flex gap-2">
                            {licenseTeam.map((person) => (
                              <img key={person.name} src={person.avatar} alt={person.name} title={person.name} className="h-8 w-8 rounded-xl border border-amber-300 object-cover" />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'maturite' && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                  <Target className="w-5 h-5" />
                  Maturité – à préciser
                </h3>
                <p className="text-slate-100/90">
                  Ici on documentera les niveaux de maturité de la fonction Contrats BZ (0 → 5), avec des critères
                  visibles et des « à faire maintenant » pour chaque palier.
                </p>
              </section>
            )}

            {activeTab === 'raci' && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                  <Users className="w-5 h-5" />
                  Rôles & responsabilités – à préciser
                </h3>
                <p className="text-slate-100/90">
                  Cette section décrira le RACI officiel pour la gestion des contrats (Mélissa, LAH, Richard, François,
                  Dany, etc.), en s&apos;alignant sur le RACI Admin/RH existant.
                </p>
              </section>
            )}

            {activeTab === 'roadmap' && (
              <section className="space-y-6">
                <div className="relative overflow-hidden rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-slate-900 to-emerald-500/10 p-5">
                  <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-amber-400/20 blur-3xl" />
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.35em] text-amber-200">Plan d’action 2026</p>
                      <h3 className="mt-2 flex items-center gap-2 text-2xl font-black text-white">
                        <Target className="h-6 w-6 text-amber-200" />
                        Faire vivre la gouvernance contrats — une coche à la fois
                      </h3>
                      <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-200">
                        Roadmap simple alignée directement sur les 8 axes du cadre holistique. La procédure est maintenant validée; l’enjeu 2026 est de la communiquer, la mettre en production avec NORA ou un autre support opérationnel, puis suivre l’avancement sans complexifier inutilement.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-center">
                      <p className="text-3xl font-black text-white">2026</p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-emerald-100">Progression visible</p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-4">
                  {roadmapQuarters.map((quarter) => (
                    <div key={quarter.quarter} className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500/20 text-sm font-black text-amber-100 ring-1 ring-amber-300/30">{quarter.quarter}</span>
                        <div>
                          <p className="font-black text-white">{quarter.title}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-300">{quarter.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-3xl border border-amber-400/25 bg-amber-500/10 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-200">Prochaine étape immédiate</p>
                      <h4 className="mt-1 text-xl font-black text-white">Communication aux parties prenantes + exécution de la procédure</h4>
                      <p className="mt-2 max-w-5xl text-sm leading-6 text-slate-200">
                        La validation par Naomie, LAH et Richard est complétée. La prochaine marche est l’adoption : communiquer aux départements, clarifier les canaux, puis faire exécuter la procédure avec NORA ou tout autre mécanisme opérationnel gouverné.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-bold">
                      <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-emerald-100">NORA active</span>
                      <span className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-cyan-100">BotOps encadré</span>
                      <span className="rounded-full border border-rose-300/30 bg-rose-400/10 px-3 py-1 text-rose-100">approbation humaine</span>
                    </div>
                  </div>
                </div>

                <div className="grid gap-5 xl:grid-cols-2">
                  {roadmap2026.map((pillar) => (
                    <div key={pillar.id} className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-200">{pillar.owner}</p>
                          <h4 className="mt-1 text-lg font-black text-white">{pillar.pillar}</h4>
                          <p className="mt-2 text-sm leading-6 text-slate-300">{pillar.focus}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-2xl font-black text-white">{pillar.progress}%</p>
                          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">avancement</p>
                        </div>
                      </div>
                      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-800">
                        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-300" style={{ width: `${pillar.progress}%` }} />
                      </div>
                      <div className="mt-4 space-y-2">
                        {pillar.items.map((item) => (
                          <div key={item.label} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-slate-950/60 p-3">
                            <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${item.status === 'done' ? 'text-emerald-300' : item.status === 'active' ? 'text-amber-300' : item.status === 'next' ? 'text-cyan-300' : 'text-slate-500'}`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm leading-5 text-slate-100">{item.label}</p>
                            </div>
                            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${statusStyles[item.status]}`}>
                              {statusLabels[item.status]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'rituels' && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                  <Shield className="w-5 h-5" />
                  Gouvernance & rituels – à préciser
                </h3>
                <p className="text-slate-100/90">
                  Cette section documentera les rituels de gouvernance autour des contrats (comité, fréquences, ordres du
                  jour types, décisions à tracer et indicateurs à suivre).
                </p>
              </section>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
